import { getTheme } from "../../../graphic/theme";

import { IMAGE_NUMBER_TYPE } from "../../../graphic/constants";

import { isArray, isString } from "@visactor/vutils";

import { createRectPath } from "../../../common/shape/rect";

import { BaseRender } from "./base-render";

import { defaultImageBackgroundRenderContribution, defaultImageRenderContribution } from "./contributions";

import { ResourceLoader } from "../../../resource-loader/loader";

import { drawBackgroundImage } from "./contributions/base-contribution-render";

const repeatStr = [ "", "repeat-x", "repeat-y", "repeat" ];

export function resolveImageRepeatMode(repeatX, repeatY) {
    let repeat = 0;
    return "repeat" === repeatX && (repeat |= 1), "repeat" === repeatY && (repeat |= 2), 
    repeat ? repeatStr[repeat] : "no-repeat";
}

export function resolveImageMode({repeatX: repeatX = "no-repeat", repeatY: repeatY = "no-repeat", imageMode: imageMode}) {
    const repeatMode = resolveImageRepeatMode(repeatX, repeatY);
    return {
        repeatMode: repeatMode,
        sizingMode: "no-repeat" === repeatMode && null != imageMode ? imageMode : "fill"
    };
}

const IMAGE_MODE_TO_BACKGROUND_MODE = {
    cover: "no-repeat-cover",
    contain: "no-repeat-contain",
    fill: "no-repeat-fill",
    auto: "no-repeat-auto"
};

export function resolveBackgroundParamsByImageSizing(sizingMode) {
    return {
        backgroundMode: IMAGE_MODE_TO_BACKGROUND_MODE[sizingMode],
        backgroundFit: !1,
        backgroundKeepAspectRatio: !1
    };
}

export function shouldClipImageByLayout({repeatX: repeatX = "no-repeat", repeatY: repeatY = "no-repeat", imageMode: imageMode, imageScale: imageScale = 1, imageOffsetX: imageOffsetX = 0, imageOffsetY: imageOffsetY = 0}) {
    const {repeatMode: repeatMode, sizingMode: sizingMode} = resolveImageMode({
        repeatX: repeatX,
        repeatY: repeatY,
        imageMode: imageMode
    });
    return "no-repeat" === repeatMode && ("cover" === sizingMode || "auto" === sizingMode || 1 !== imageScale || 0 !== imageOffsetX || 0 !== imageOffsetY);
}

export function drawImageWithLayout(context, data, x, y, width, height, {repeatX: repeatX = "no-repeat", repeatY: repeatY = "no-repeat", imageMode: imageMode, imageScale: imageScale = 1, imageOffsetX: imageOffsetX = 0, imageOffsetY: imageOffsetY = 0, imagePosition: imagePosition = "top-left"}) {
    const {repeatMode: repeatMode, sizingMode: sizingMode} = resolveImageMode({
        repeatX: repeatX,
        repeatY: repeatY,
        imageMode: imageMode
    }), imageBackgroundParams = "no-repeat" === repeatMode ? resolveBackgroundParamsByImageSizing(sizingMode) : {
        backgroundMode: repeatMode,
        backgroundFit: !1,
        backgroundKeepAspectRatio: !1
    };
    drawBackgroundImage(context, data, {
        x1: x,
        y1: y,
        x2: x + width,
        y2: y + height,
        width: () => width,
        height: () => height
    }, {
        backgroundMode: imageBackgroundParams.backgroundMode,
        backgroundFit: imageBackgroundParams.backgroundFit,
        backgroundKeepAspectRatio: imageBackgroundParams.backgroundKeepAspectRatio,
        backgroundScale: imageScale,
        backgroundOffsetX: imageOffsetX,
        backgroundOffsetY: imageOffsetY,
        backgroundPosition: imagePosition
    });
}

export class DefaultCanvasImageRender extends BaseRender {
    constructor(graphicRenderContributions) {
        super(), this.graphicRenderContributions = graphicRenderContributions, this.numberType = IMAGE_NUMBER_TYPE, 
        this.builtinContributions = [ defaultImageRenderContribution, defaultImageBackgroundRenderContribution ], 
        this.init(graphicRenderContributions);
    }
    drawShape(image, context, x, y, drawContext, params, fillCb, strokeCb) {
        const imageAttribute = getTheme(image).image, {repeatX: repeatX = imageAttribute.repeatX, repeatY: repeatY = imageAttribute.repeatY, x: originX = imageAttribute.x, y: originY = imageAttribute.y, cornerRadius: cornerRadius = imageAttribute.cornerRadius, fillStrokeOrder: fillStrokeOrder = imageAttribute.fillStrokeOrder, cornerType: cornerType = imageAttribute.cornerType, imageMode: imageMode = imageAttribute.imageMode, imageScale: imageScale = imageAttribute.imageScale, imageOffsetX: imageOffsetX = imageAttribute.imageOffsetX, imageOffsetY: imageOffsetY = imageAttribute.imageOffsetY, imagePosition: imagePosition = imageAttribute.imagePosition, image: url} = image.attribute, data = this.valid(image, imageAttribute, fillCb);
        if (!data) return;
        const {fVisible: fVisible, sVisible: sVisible, doFill: doFill, doStroke: doStroke} = data;
        if (!url || !image.resources) return;
        const res = image.resources.get(url);
        if ("success" !== res.state) return;
        const width = image.width, height = image.height;
        context.beginPath();
        let needCornerClip = !1;
        0 === cornerRadius || isArray(cornerRadius) && cornerRadius.every((num => 0 === num)) ? context.rect(x, y, width, height) : (createRectPath(context, x, y, width, height, cornerRadius, "bevel" !== cornerType), 
        needCornerClip = !0), context.setShadowBlendStyle && context.setShadowBlendStyle(image, image.attribute, imageAttribute);
        const _runFill = () => {
            doFill && (fillCb ? fillCb(context, image.attribute, imageAttribute) : fVisible && (context.setCommonStyle(image, image.attribute, x, y, imageAttribute), 
            drawImageWithLayout(context, res.data, x, y, width, height, {
                repeatX: repeatX,
                repeatY: repeatY,
                imageMode: imageMode,
                imageScale: imageScale,
                imageOffsetX: imageOffsetX,
                imageOffsetY: imageOffsetY,
                imagePosition: imagePosition
            })));
        }, _runStroke = () => {
            doStroke && (strokeCb ? strokeCb(context, image.attribute, imageAttribute) : sVisible && (context.setStrokeStyle(image, image.attribute, originX - x, originY - y, imageAttribute), 
            context.stroke()));
        }, needLayoutClip = shouldClipImageByLayout({
            repeatX: repeatX,
            repeatY: repeatY,
            imageMode: imageMode,
            imageScale: imageScale,
            imageOffsetX: imageOffsetX,
            imageOffsetY: imageOffsetY,
            imagePosition: imagePosition
        }), needClip = needCornerClip || needLayoutClip;
        fillStrokeOrder ? (_runStroke(), needClip && (context.save(), context.clip()), this.beforeRenderStep(image, context, x, y, doFill, !1, fVisible, !1, imageAttribute, drawContext, fillCb), 
        _runFill(), needClip && context.restore()) : (needClip && (context.save(), context.clip()), 
        this.beforeRenderStep(image, context, x, y, doFill, !1, fVisible, !1, imageAttribute, drawContext, fillCb), 
        _runFill(), needClip && context.restore(), _runStroke()), this.afterRenderStep(image, context, x, y, doFill, !1, fVisible, !1, imageAttribute, drawContext, fillCb);
    }
    draw(image, renderService, drawContext) {
        const {image: url} = image.attribute;
        if (!url || !image.resources) return;
        const res = image.resources.get(url);
        if ("loading" === res.state && isString(url)) return void ResourceLoader.improveImageLoading(url);
        if ("success" !== res.state) return;
        const {context: context} = renderService.drawParams;
        if (!context) return;
        const imageAttribute = getTheme(image).image;
        this._draw(image, imageAttribute, !1, drawContext);
    }
}
//# sourceMappingURL=image-render.js.map
